import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { useFormV2, FormField, FormGrid, FormActions } from "@/lib/form-v2";
import { Loader2 } from "lucide-react";
import type { Queue } from "@/lib/api/system";
import {
  queueSchemaConfig,
  buildFieldMeta,
  defaultFormValues,
  type QueueFormValues,
} from "./form-schema";
import { apiToForm } from "./transform";

interface UpdateQueueFormProps {
  queue: Queue;
  isSubmitting: boolean;
  onSubmit: (values: QueueFormValues) => void;
}

export function UpdateQueueForm({
  queue,
  isSubmitting,
  onSubmit,
}: UpdateQueueFormProps) {
  const fieldMeta = buildFieldMeta();
  const initialValues = apiToForm(queue);

  const form = useFormV2<QueueFormValues>(
    queueSchemaConfig,
    fieldMeta,
    { ...defaultFormValues, ...initialValues }
  );

  const handleSubmit = () => {
    form.attemptSubmit();
    const result = form.validate();
    if (!result.success) return;
    onSubmit(form.values);
  };

  const renderField = (name: keyof QueueFormValues) => {
    const props = form.getFieldProps(name as string);
    if (!props) return null;
    return (
      <FormField
        key={name}
        fieldProps={props}
        onChange={(v) => form.setFieldValue(name as string, v)}
        onBlur={() => form.touchField(name as string)}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-4 p-4 bg-muted rounded-md">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium">ID</p>
            <p className="text-sm text-muted-foreground mt-1">{queue.id}</p>
          </div>
          <div>
            <p className="text-sm font-medium">Name</p>
            <p className="text-sm text-muted-foreground mt-1">{queue.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium">Type</p>
            <p className="text-sm text-muted-foreground mt-1">{queue.type}</p>
          </div>
        </div>
      </div>

      <FormGrid cols={1}>
        {renderField("max_concurrent")}
      </FormGrid>

      <FormActions>
        <DialogClose asChild>
          <Button variant="outline" disabled={isSubmitting}>
            Cancel
          </Button>
        </DialogClose>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save
        </Button>
      </FormActions>
    </div>
  );
}
